import { useContext, useRef, useEffect } from "react";
import Main from "../../Main/components";
import { APPLICATION_NAME, MAIN_ID, THEME_CACHE_KEYS } from "../../util/constants";
import {
  withOSUTheming,
  useScrollToHash,
  Mui,
  withTableOfContents,
  withPageHeading,
  PageHeadingContext,
  useFooterWithTOC,
  MuiIcons
} from "@osu/react-ui";
import withAmplifyCacheValues from "../../util/DarkModeToggle";
import { AppProvider } from "../context";
import { Navbar as OSUNavBar } from "@osu/navbar";
import Footer from "@osu/footer";
import Navbar from "../../Navbar/components";
import useCssVariableUpdater from "../hooks/useCssVariableUpdater";
import useCompleteAuthFlowHandler from "../../Authentication/hooks/useCompleteAuthFlowHandler";
import '../styles/index.scss'
import GlobalAlertHandler from "../../GlobalAlertHandler";

function App(props = {}) {
  const { navHeight, darkMode, updateDarkMode } = props;
  const { ref } = useScrollToHash();
  const { setApplicationName } = useContext(PageHeadingContext);
  const footerRef = useRef()
  useFooterWithTOC({ ref: footerRef })
  const trigger = Mui.useScrollTrigger({
    disableHysteresis: true,
    threshold: navHeight,
  });

  useEffect(() => {
    setApplicationName(APPLICATION_NAME);
  }, [setApplicationName]);

  // Get current setting from localStorage or environment variable
  const defaultThemeFromEnv = process.env.REACT_APP_DEFAULT_THEME;
  let currentSetting = localStorage.getItem(THEME_CACHE_KEYS.darkMode) || defaultThemeFromEnv || THEME_CACHE_KEYS.system;

  // Apply the theme setting on component mount
  useEffect(() => {
    if (updateDarkMode && currentSetting) {
      console.log("Initializing theme to:", currentSetting);
      updateDarkMode(currentSetting);
    }
  }, [updateDarkMode, currentSetting]);

  const { FooterProps = {} } = props;

  return (
    <div className="App" ref={ref}>
      <OSUNavBar darkMode={darkMode} />
      <Navbar />
      <Main spacingTop={!trigger} className="anchor-link" id={MAIN_ID} />
      <Footer
        darkMode={darkMode}
        ref={footerRef}
        style={{ position: 'relative', zIndex: 10 }}
        {...FooterProps}
      />
      <Mui.Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '20px',
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        ...(process.env.REACT_APP_DEFAULT_THEME ? { visibility: 'hidden' } : {}),
      }}>
        <Mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1,  }}>
          <label id="dark-mode-toggle-label">
            <MuiIcons.DarkMode sx={{ mr: '.5em' }} fontSize="inherit" />
            Theme:{" "}
          </label>

          <Mui.Select
            id="footer-dark-mode-select"
            labelId="dark-mode-toggle-label"
            value={currentSetting}
            onChange={(event) => {
              console.log("Dark mode changed to:", event.target.value);
              const newValue = event.target.value;
              if (updateDarkMode) {
                updateDarkMode(newValue);
              } else {
                console.warn('updateDarkMode function not available');
                // Fallback
                localStorage.setItem(THEME_CACHE_KEYS.darkMode, newValue);
                window.location.reload();
              }
            }}
            size="small"
            sx={{ minWidth: '150px' }}
          >
            <Mui.MenuItem value={THEME_CACHE_KEYS.dark}>
              <Mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MuiIcons.DarkMode sx={{ mr: 1 }} />
                Dark Mode
              </Mui.Box>
            </Mui.MenuItem>
            <Mui.MenuItem value={THEME_CACHE_KEYS.light}>
              <Mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MuiIcons.LightMode sx={{ mr: 1 }} />
                Light Mode
              </Mui.Box>
            </Mui.MenuItem>
            <Mui.MenuItem value={THEME_CACHE_KEYS.system}>
              <Mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MuiIcons.SettingsBrightness sx={{ mr: 1 }} />
                System Default
              </Mui.Box>
            </Mui.MenuItem>
          </Mui.Select>
        </Mui.Box>
      </Mui.Box>
    </div>
  );
}

const TOCWrappedApp = withTableOfContents(App);

function Provider(props = {}) {
  const { darkMode, updateDarkMode } = props;
  const { navHeight } = useCssVariableUpdater({ darkMode });

  useCompleteAuthFlowHandler()

  return (
    <AppProvider value={{ darkMode, updateDarkMode }}>
      <GlobalAlertHandler />
      <TOCWrappedApp darkMode={darkMode} updateDarkMode={updateDarkMode} navHeight={navHeight} />
    </AppProvider>
  );
}

// Prevent errors generated from unmodifiable parent library
let consoleError = undefined;
setUpConsoleErrorToIgnoreDefaultPropsWarnings();

function setUpConsoleErrorToIgnoreDefaultPropsWarnings(...args) {
  if (process.env.REACT_APP_IGNORE_DEFAULT_PROPS_ERRORS !== "true") { return; } // Flag not set, leave console.error as-is
  if (consoleError || console.error === consoleErrorIgnoreDefaultProps) { return; } // We're already set up, ignore
  consoleError = console.error;
  console.error = consoleErrorIgnoreDefaultProps;
}

function consoleErrorIgnoreDefaultProps(...args) {
  if (args?.[0] && JSON.stringify(args[0]).includes("Support for defaultProps will be removed from function components in a future major release. Use JavaScript default parameters instead.")) { return; }
  consoleError(...args);
}

export default withAmplifyCacheValues(
  withOSUTheming(withPageHeading(Provider))
);